import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Zap, Loader2, IndianRupee, Copy, Check, QrCode, AlertCircle, 
  X, Download, Share2, Smartphone, ExternalLink, ChevronDown
} from 'lucide-react';

// UPI App configurations with their specific URL schemes and logo files
const UPI_APPS = [
  { id: 'phonepe', name: 'PhonePe',    scheme: 'phonepe://pay', logo: '/logos/phonepe.svg' },
  { id: 'gpay',    name: 'Google Pay', scheme: 'tez://upi/pay',  logo: '/logos/gpay.svg'   },
  { id: 'paytm',   name: 'Paytm',      scheme: 'paytmmp://pay',  logo: '/logos/paytm.svg'  },
  { id: 'other',   name: 'Any UPI',    scheme: 'upi://pay',      logo: '/logos/upi.svg'    },
];

// Canonical domain for universal links
const SPLITPAY_DOMAIN = 'https://splitpay.co.in';

const PaymentPage = () => {
  const { expenseId } = useParams();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [showFallbackMessage, setShowFallbackMessage] = useState(false);
  const [isWhatsAppBrowser, setIsWhatsAppBrowser] = useState(false);
  const [dismissedBanner, setDismissedBanner] = useState(false);
  const qrRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Detect WhatsApp in-app browser
  useEffect(() => {
    const ua = navigator.userAgent || '';
    if (ua.toLowerCase().includes('whatsapp')) {
      setIsWhatsAppBrowser(true);
    }
  }, []);

  useEffect(() => {
    fetchPaymentData();
  }, [expenseId]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/pay/${expenseId}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Payment not found');
      }
      const data = await response.json();
      setPaymentData(data);
    } catch (err) {
      setError(err.message || 'Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = Math.abs(amount);
    if (num === Math.floor(num)) {
      return `₹${num.toLocaleString('en-IN')}`;
    }
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format amount for UPI (keep current format - integers for whole numbers)
  const formatUPIAmount = (amount) => {
    if (amount === Math.floor(amount)) {
      return String(Math.floor(amount));
    }
    return amount.toFixed(2);
  };

  // Build UPI link with specific app scheme
  const buildUPILink = (scheme) => {
    if (!paymentData) return '';
    const { payee_upi_id, payee_name, amount, description } = paymentData;
    const params = new URLSearchParams({
      pa: payee_upi_id,
      pn: payee_name,
      am: formatUPIAmount(amount),
      cu: 'INR',
      tn: description || 'SplitPay Payment'
    });
    return `${scheme}?${params.toString()}`;
  };

  // Handle main CTA click - try universal link first
  const handlePayWithSplitPay = () => {
    const universalLink = `${SPLITPAY_DOMAIN}/pay/${expenseId}`;
    
    // Try to open the universal link
    window.location.href = universalLink;
    
    // After 700ms, if still on page, show fallback message
    setTimeout(() => {
      setShowFallbackMessage(true);
    }, 700);
  };

  // Handle specific UPI app payment
  const handleUPIAppPayment = (app) => {
    const upiLink = buildUPILink(app.scheme);
    window.location.href = upiLink;
  };

  // Copy functions
  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyAll = () => {
    if (!paymentData) return;
    const { payee_name, payee_upi_id, amount, description } = paymentData;
    const text = `Pay ${formatCurrency(amount)} to ${payee_name}\nUPI ID: ${payee_upi_id}\nNote: ${description || 'SplitPay Payment'}`;
    handleCopy(text, 'all');
  };

  // QR Save function
  const handleSaveQR = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pay/${expenseId}/qr`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `splitpay-${expenseId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to save QR:', err);
    }
  };

  // QR Share function
  const handleShareQR = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pay/${expenseId}/qr`);
      const blob = await response.blob();
      const file = new File([blob], `splitpay-${expenseId}.png`, { type: 'image/png' });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'SplitPay Payment QR',
          text: `Pay ${formatCurrency(paymentData?.amount)} to ${paymentData?.payee_name}`
        });
      } else {
        // Fallback to download
        handleSaveQR();
      }
    } catch (err) {
      console.error('Failed to share QR:', err);
      handleSaveQR();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-fuchsia-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-fuchsia-50 flex flex-col items-center justify-center p-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <span className="text-3xl">😕</span>
        </div>
        <h1 className="text-2xl font-heading font-bold mb-2">Oops!</h1>
        <p className="text-neutral-600 text-center">{error}</p>
      </div>
    );
  }

  if (paymentData?.is_settled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col items-center justify-center p-6">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
          <Check className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-heading font-bold mb-2 text-emerald-800">Already Paid!</h1>
        <p className="text-emerald-600 text-center">This payment has already been settled.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50">
      {/* WhatsApp Browser Banner */}
      {isWhatsAppBrowser && !dismissedBanner && (
        <div className="bg-amber-500 text-white px-4 py-3 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-medium">UPI app blocked?</p>
            <p className="text-amber-100">Tap ⋮ and open in Chrome/Safari, or use "Pay with SplitPay" below.</p>
          </div>
          <button 
            onClick={() => setDismissedBanner(true)}
            className="p-1 hover:bg-amber-600 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-neutral-200 px-6 py-4">
        <div className="max-w-md mx-auto flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-fuchsia-500 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-heading font-bold">
            Split<span className="text-violet-600">Pay</span>
          </span>
        </div>
      </header>

      <main className="max-w-md mx-auto p-6 space-y-6">
        {/* Payment Card */}
        <div className="bg-white rounded-3xl border-2 border-black shadow-hard p-6 space-y-6">
          {/* Amount */}
          <div className="text-center">
            <p className="text-sm text-neutral-500 uppercase tracking-wide mb-1">Amount to Pay</p>
            <p className="text-5xl font-heading font-bold bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
              {formatCurrency(paymentData?.amount || 0)}
            </p>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />

          {/* Payment Details */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-neutral-500">Pay to</span>
              <span className="font-bold">{paymentData?.payee_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-neutral-500">For</span>
              <span className="font-medium">{paymentData?.description}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-neutral-500">UPI ID</span>
              <span className="font-mono text-sm bg-neutral-100 px-2 py-1 rounded">
                {paymentData?.payee_upi_id}
              </span>
            </div>
          </div>

          {/* Primary CTA */}
          <button
            onClick={() => setShowBottomSheet(true)}
            className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-bold text-lg py-4 rounded-2xl border-2 border-black shadow-hard hover:shadow-hard-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center justify-center gap-3"
            data-testid="pay-with-splitpay-button"
          >
            <IndianRupee className="w-6 h-6" />
            Pay {formatCurrency(paymentData?.amount || 0)} with SplitPay
            <ChevronDown className="w-5 h-5" />
          </button>

          {/* Fallback Message */}
          {showFallbackMessage && (
            <div className="text-center text-sm text-amber-600 bg-amber-50 p-3 rounded-xl border border-amber-200">
              <p>Try using QR code or copy details below to pay manually.</p>
            </div>
          )}
        </div>

        {/* QR Code Section */}
        <div className="bg-white rounded-3xl border-2 border-black shadow-hard p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold flex items-center gap-2">
              <QrCode className="w-5 h-5 text-violet-600" />
              Scan to Pay
            </h3>
          </div>

          <div className="text-center" ref={qrRef}>
            <img 
              src={`${API_URL}/api/pay/${expenseId}/qr`}
              alt="UPI QR Code"
              className="w-48 h-48 mx-auto rounded-lg shadow-md"
            />
            <p className="text-xs text-neutral-500 mt-3">
              Open your UPI app → Scan QR → Gallery
            </p>
          </div>

          {/* QR Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleSaveQR}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-black bg-white hover:bg-neutral-50 font-medium transition-all flex items-center justify-center gap-2"
              data-testid="save-qr-button"
            >
              <Download className="w-4 h-4" />
              Save QR
            </button>
            <button
              onClick={handleShareQR}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-black bg-white hover:bg-neutral-50 font-medium transition-all flex items-center justify-center gap-2"
              data-testid="share-qr-button"
            >
              <Share2 className="w-4 h-4" />
              Share QR
            </button>
          </div>
        </div>

        {/* Copy Details Section */}
        <div className="bg-white rounded-3xl border-2 border-black shadow-hard p-6 space-y-4">
          <h3 className="font-heading font-bold flex items-center gap-2">
            <Copy className="w-5 h-5 text-violet-600" />
            Copy Details
          </h3>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleCopy(paymentData?.payee_upi_id, 'upi')}
              className={`py-3 px-4 rounded-xl border-2 font-medium transition-all flex items-center justify-center gap-2 ${
                copied === 'upi' 
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                  : 'border-black bg-white hover:bg-neutral-50'
              }`}
              data-testid="copy-upi-button"
            >
              {copied === 'upi' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              UPI ID
            </button>
            <button
              onClick={() => handleCopy(formatUPIAmount(paymentData?.amount), 'amount')}
              className={`py-3 px-4 rounded-xl border-2 font-medium transition-all flex items-center justify-center gap-2 ${
                copied === 'amount' 
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                  : 'border-black bg-white hover:bg-neutral-50'
              }`}
              data-testid="copy-amount-button"
            >
              {copied === 'amount' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Amount
            </button>
            <button
              onClick={() => handleCopy(paymentData?.description || '', 'note')}
              className={`py-3 px-4 rounded-xl border-2 font-medium transition-all flex items-center justify-center gap-2 ${
                copied === 'note' 
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                  : 'border-black bg-white hover:bg-neutral-50'
              }`}
              data-testid="copy-note-button"
            >
              {copied === 'note' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Note
            </button>
            <button
              onClick={handleCopyAll}
              className={`py-3 px-4 rounded-xl border-2 font-medium transition-all flex items-center justify-center gap-2 ${
                copied === 'all' 
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                  : 'border-violet-500 bg-violet-50 text-violet-700 hover:bg-violet-100'
              }`}
              data-testid="copy-all-button"
            >
              {copied === 'all' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copy All
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-neutral-400 pt-4 pb-8">
          <p>Secure payment via UPI</p>
          <p className="mt-1">Powered by SplitPay</p>
        </div>
      </main>

      {/* Bottom Sheet Modal */}
      {showBottomSheet && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowBottomSheet(false)}
          />
          
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl border-t-2 border-x-2 border-black shadow-2xl animate-slide-up">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-neutral-300 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="px-6 pb-4 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">Pay with</h2>
                <button 
                  onClick={() => setShowBottomSheet(false)}
                  className="w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-neutral-500 mt-1">
                {formatCurrency(paymentData?.amount || 0)} to {paymentData?.payee_name}
              </p>
            </div>
            
            {/* Payment Details Summary */}
            <div className="px-6 py-4 bg-neutral-50 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-500">UPI ID</span>
                <span className="font-mono">{paymentData?.payee_upi_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Note</span>
                <span>{paymentData?.description}</span>
              </div>
            </div>
            
            {/* UPI App Buttons - 2x2 Grid */}
            <div className="px-6 py-6">
              <div className="grid grid-cols-2 gap-4">
                {UPI_APPS.map((app) => (
                  <button
                    key={app.id}
                    onClick={() => handleUPIAppPayment(app)}
                    className="bg-white border-2 border-neutral-200 rounded-2xl p-3 hover:border-violet-400 hover:bg-violet-50 transition-all flex flex-col items-center justify-center gap-2 h-24"
                    data-testid={`pay-${app.id}-button`}
                  >
                    <img
                      src={app.logo}
                      alt={app.name}
                      className="h-9 max-w-full object-contain"
                    />
                    <span className="text-xs font-medium text-neutral-500">{app.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Quick Copy in Sheet */}
            <div className="px-6 pb-8 pt-2 border-t border-neutral-200">
              <div className="flex gap-2">
                <button
                  onClick={() => handleCopy(paymentData?.payee_upi_id, 'upi')}
                  className="flex-1 py-2 px-3 rounded-lg border border-neutral-300 text-sm font-medium hover:bg-neutral-50 flex items-center justify-center gap-1"
                >
                  {copied === 'upi' ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                  UPI ID
                </button>
                <button
                  onClick={() => handleCopy(formatUPIAmount(paymentData?.amount), 'amount')}
                  className="flex-1 py-2 px-3 rounded-lg border border-neutral-300 text-sm font-medium hover:bg-neutral-50 flex items-center justify-center gap-1"
                >
                  {copied === 'amount' ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                  Amount
                </button>
                <button
                  onClick={handleCopyAll}
                  className="flex-1 py-2 px-3 rounded-lg border border-violet-300 bg-violet-50 text-violet-700 text-sm font-medium hover:bg-violet-100 flex items-center justify-center gap-1"
                >
                  {copied === 'all' ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                  All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for bottom sheet animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PaymentPage;
